import { Paper, lighten } from "@mui/material";
import * as React from "react";
import { memberTheme } from "../member/memberTheme";

/**
 * Very small drag-and-drop reorderer widget that supports desktop and mobile.
 */
export function DragReorderer(props: { options: string[]; onSetOrder: (order: string[]) => void }) {
  const [order, setOrder] = React.useState<string[]>(props.options);
  const itemsRef = React.useRef<Array<HTMLDivElement | null>>([]);

  React.useEffect(() => {
    itemsRef.current = itemsRef.current.slice(0, props.options.length);
  }, [props.options]);

  function findDropTargetIndex(ignoreIdx: number, absX: number, absY: number): number | undefined {
    let draggedOnto = undefined;

    itemsRef.current.forEach((elem, idx) => {
      if (idx === ignoreIdx) return;

      const rect = elem?.getBoundingClientRect();

      if (rect && absX >= rect.left && absX <= rect.right && absY >= rect.top && absY <= rect.bottom) {
        draggedOnto = idx;
      }
    });

    return draggedOnto;
  }

  function moveItemTo(fromIdx: number, toIdx: number) {
    // swap the elements
    const newOrder = [...order];
    const item = order[fromIdx];

    if (fromIdx > toIdx) {
      newOrder.splice(fromIdx, 1);
      newOrder.splice(toIdx, 0, item);
    } else {
      newOrder.splice(toIdx + 1, 0, item);
      newOrder.splice(fromIdx, 1);
    }

    setOrder(newOrder);
    props.onSetOrder(newOrder);
  }

  function highlightIndex(draggingOverIdx: number | undefined) {
    if (itemsRef.current) {
      itemsRef.current.forEach((elem, i) => {
        if (elem !== null) {
          elem.style.opacity = draggingOverIdx === i ? "0.5" : "1";
        }
      });
    }
  }

  return (
    <>
      {order.map((opt, index) => (
        <div key={opt} style={{ minHeight: "4rem" }}>
          <Paper
            component="div"
            elevation={0}
            sx={{
              backgroundColor: lighten(memberTheme.palette.greys.grey100, 0.85),
              padding: "1rem",
              marginBottom: "1.5rem",
            }}
            ref={(el: HTMLDivElement) => {
              if (el !== null) {
                itemsRef.current[index] = el;
              }
            }}
            draggable
            style={{ userSelect: "none" }}
            // events for mobile drag'n'drop support
            onTouchStart={(e: React.TouchEvent<HTMLDivElement>) => {}}
            onTouchMove={(e: React.TouchEvent<HTMLDivElement>) => {
              e.preventDefault();

              const windowX = e.targetTouches[0].pageX;
              const windowY = e.targetTouches[0].pageY;

              const draggingOverIdx = findDropTargetIndex(index, windowX - window.scrollX, windowY - window.scrollY);

              // highlight the target element with opacity change
              highlightIndex(draggingOverIdx);

              // @ts-ignore
              const rect = e.target.getBoundingClientRect();
              // @ts-ignore
              e.target.style.position = "absolute";
              // @ts-ignore
              e.target.style.left = windowX - rect.width / 2 + "px";
              // @ts-ignore
              e.target.style.top = windowY - rect.height / 2 + "px";
            }}
            onTouchEnd={(e: React.TouchEvent<HTMLDivElement>) => {
              e.preventDefault();

              // @ts-ignore
              const windowX = e.changedTouches[0].pageX;
              // @ts-ignore
              const windowY = e.changedTouches[0].pageY;

              const draggedOnto = findDropTargetIndex(index, windowX - window.scrollX, windowY - window.scrollY);

              if (draggedOnto !== undefined) {
                moveItemTo(index, draggedOnto);
              }

              // make element render in position again
              // @ts-ignore
              e.target.style.position = "static";

              // reset opacity
              highlightIndex(undefined);
            }}
            // events for desktop drag'n'drop support
            onDragStart={(e: React.DragEvent<HTMLDivElement>) => {
              e.dataTransfer.setData("id", index.toString());
            }}
            onDragEnd={(e: React.DragEvent<HTMLDivElement>) => {
              highlightIndex(undefined);
            }}
            onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
              e.preventDefault();

              const draggingOverIdx = itemsRef.current.findIndex((r) => r === e.target);

              // highlight element being dragged over
              highlightIndex(draggingOverIdx);
            }}
            onDrop={(e: React.DragEvent<HTMLDivElement>) => {
              const fromIdx = Number.parseInt(e.dataTransfer.getData("id"));
              const toIdx = index;

              moveItemTo(fromIdx, toIdx);
            }}
          >
            {`${index + 1}: ${opt}`}
          </Paper>
        </div>
      ))}
    </>
  );
}
